import React, { useState } from 'react';
import { authFetch } from '../../utils/auth';
import Button from '../../components/common/Button';

/**
 * RestartDialog — confirms "Start på nytt" for one child's søknad, then
 * deletes the draft and creates a new, empty one for the same child.
 * onRestarted(newDraftId) gets the new draft's id.
 *
 * A retry after a failure is safe: a draft that is already gone (404) is
 * skipped, and creating is find-or-create on the child.
 */
export default function RestartDialog({
  type,
  draftId,
  subject,
  childName,
  hasGeneratedText,
  onCancel,
  onRestarted,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);

    try {
      try {
        await authFetch(`/soknad/${type}/drafts/${draftId}`, { method: 'DELETE' });
      } catch (err) {
        if (err?.status !== 404) throw err;
      }
      const response = await authFetch(`/soknad/${type}/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subject),
      });
      const newDraft = await response.json();
      onRestarted(newDraft._id);
    } catch (err) {
      console.error('[RestartDialog] Restart error:', err);
      setError('Det gikk ikke å starte på nytt. Prøv igjen.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="restart-dialog-title"
        className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
      >
        <h3 id="restart-dialog-title" className="text-lg font-semibold text-text-primary mb-2">
          Vil du starte på nytt for {childName}?
        </h3>
        <p className="text-text-secondary mb-6">
          {hasGeneratedText
            ? 'Svarene og den ferdige teksten slettes. Har du lastet ned søknaden, beholder du den filen.'
            : 'Svarene dine slettes.'}
        </p>
        {error && (
          <p role="alert" className="text-sm text-error-600 mb-4">{error}</p>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
          <Button variant="tertiary" autoFocus onClick={onCancel} disabled={busy}>
            Avbryt
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={busy}>
            {busy ? 'Starter på nytt …' : 'Ja, start på nytt'}
          </Button>
        </div>
      </div>
    </div>
  );
}
